import { ArrowUpRight, ArrowDownRight, ArrowLeftRight } from 'lucide-react';
import type { MercuryTransaction } from '@/lib/mercuryService';

function formatCurrencyValue(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function getTransactionIcon(amount: number, kind: string) {
  if (kind === 'internalTransfer' || kind === 'externalTransfer') {
    return { icon: ArrowLeftRight, bg: 'bg-sky-500/10', color: 'text-sky-600' };
  }
  if (amount > 0) {
    return { icon: ArrowDownRight, bg: 'bg-emerald-500/10', color: 'text-emerald-800' };
  }
  return { icon: ArrowUpRight, bg: 'bg-red-500/10', color: 'text-red-600' };
}

function formatTransactionDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface MercuryTransactionsSectionProps {
  transactions: MercuryTransaction[];
  loading: boolean;
}

export default function MercuryTransactionsSection({ transactions, loading }: MercuryTransactionsSectionProps) {
  if (loading) {
    return (
      <div className="glass-panel rounded-2xl p-8">
        <div className="flex items-center justify-center gap-3">
          <div className="w-5 h-5 border-2 border-white/10 border-t-sky-600 rounded-full animate-spin" />
          <span className="text-sm text-white">Loading transactions...</span>
        </div>
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="glass-panel rounded-2xl p-8">
        <div className="flex flex-col items-center justify-center">
          <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mb-3">
            <ArrowLeftRight size={24} className="text-white" />
          </div>
          <h3 className="text-sm font-semibold text-white mb-1">No transactions</h3>
          <p className="text-xs text-white">No activity found for this period</p>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-panel rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-sky-500/10/80 flex items-center justify-center">
          <ArrowLeftRight size={16} className="text-sky-600" />
        </div>
        <h2 className="text-sm font-bold text-white">Recent Transactions</h2>
        <span className="text-[11px] text-white ml-auto">{transactions.length} total</span>
      </div>
      <div className="space-y-2 max-h-[480px] overflow-y-auto">
        {transactions.map((txn) => {
          const { icon: Icon, bg, color } = getTransactionIcon(txn.amount, txn.kind);
          const isCredit = txn.amount > 0;

          return (
            <div
              key={txn.id}
              className="flex items-center justify-between p-3.5 glass-inner rounded-xl glass-hover"
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center shrink-0`}>
                  <Icon size={15} className={color} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-white truncate">
                    {txn.counterpartyName || txn.bankDescription || txn.kind}
                  </p>
                  <div className="flex items-center gap-1.5">
                    <p className="text-[10px] text-white">
                      {formatTransactionDate(txn.createdAt)}
                    </p>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                      txn.status === 'sent' || txn.status === 'completed'
                        ? 'bg-emerald-500/10 text-emerald-800'
                        : txn.status === 'pending'
                          ? 'bg-amber-500/10 text-amber-600'
                          : txn.status === 'failed' || txn.status === 'cancelled'
                            ? 'bg-red-500/10 text-red-600'
                            : 'bg-white/10 text-white'
                    }`}>
                      {txn.status}
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-right shrink-0 ml-3">
                <p className={`text-xs font-bold tabular-nums ${isCredit ? 'text-emerald-700' : 'text-red-600'}`}>
                  {isCredit ? '+' : ''}{formatCurrencyValue(txn.amount)}
                </p>
                {txn.note && (
                  <p className="text-[10px] text-white truncate max-w-[120px]">{txn.note}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
